import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';

import 'highlight.js/styles/shades-of-purple.css';
import './style.css';

import App from './App';
import { isCanvasFullscreen } from './fullscreenControl';

const RENDER_EVENT = new CustomEvent('render');

export function observeFractalCanvases(rootElement) {
  if (typeof IntersectionObserver === 'undefined') {
    const poll = () => {
      const viewportHeight = window.innerHeight;
      for (const canvas of document.getElementsByTagName('canvas')) {
        if (canvas?.dataset?.deepCanvas !== undefined) continue;
        const rect = canvas.getBoundingClientRect();
        if (rect.bottom >= 0 && rect.top <= viewportHeight) {
          canvas.dispatchEvent(RENDER_EVENT);
        }
      }
      setTimeout(poll, 200);
    };
    poll();
    return;
  }

  const observedCanvases = new WeakSet();
  const visibleCanvases = new Set();
  const pendingCanvases = new Set();
  const MAX_RENDERS_PER_FRAME = 4;
  let scheduledFrame = null;

  const flushPending = () => {
    scheduledFrame = null;
    let renderedCount = 0;
    for (const canvas of pendingCanvases) {
      pendingCanvases.delete(canvas);
      if (visibleCanvases.has(canvas) && canvas.isConnected) {
        canvas.dispatchEvent(RENDER_EVENT);
        if (++renderedCount >= MAX_RENDERS_PER_FRAME && pendingCanvases.size > 0) {
          scheduledFrame = requestAnimationFrame(flushPending);
          break;
        }
      }
    }
  };

  const scheduleRender = (canvas) => {
    if (!visibleCanvases.has(canvas)) return;
    pendingCanvases.add(canvas);
    if (scheduledFrame === null) {
      scheduledFrame = requestAnimationFrame(flushPending);
    }
  };

  const intersectionObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const canvas = entry.target;
      if (entry.isIntersecting || isCanvasFullscreen(canvas)) {
        visibleCanvases.add(canvas);
        scheduleRender(canvas);
      } else {
        visibleCanvases.delete(canvas);
        pendingCanvases.delete(canvas);
      }
    }
  }, { rootMargin: '200px 0px' });

  const registerCanvas = (canvas) => {
    if (canvas?.dataset?.deepCanvas !== undefined) return;
    if (!observedCanvases.has(canvas)) {
      observedCanvases.add(canvas);
      intersectionObserver.observe(canvas);
    }
  };

  const unregisterCanvas = (canvas) => {
    observedCanvases.delete(canvas);
    visibleCanvases.delete(canvas);
    pendingCanvases.delete(canvas);
    intersectionObserver.unobserve?.(canvas);
  };

  const forEachCanvas = (node, callback) => {
    if (!node || node.nodeType !== 1) return;
    if (node.tagName === 'CANVAS') {
      callback(node);
      return;
    }
    const canvases = node.getElementsByTagName?.('canvas');
    if (!canvases) return;
    for (const canvas of canvases) {
      callback(canvas);
    }
  };

  window.addEventListener('fractal-needs-render', (event) => {
    const target = event.target;
    if (target && target.tagName === 'CANVAS' && target.dataset?.deepCanvas === undefined) {
      registerCanvas(target);
      if (!visibleCanvases.has(target) && target.isConnected) {
        const rect = target.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement?.clientHeight || 0;
        if (isCanvasFullscreen(target)
          || !Number.isFinite(rect.bottom) || (rect.bottom >= -200 && rect.top <= vh + 200)) {
          visibleCanvases.add(target);
        }
      }
      scheduleRender(target);
    }
  });

  forEachCanvas(rootElement || document.body, registerCanvas);
  if (typeof MutationObserver !== 'undefined') {
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const added of mutation.addedNodes) {
          forEachCanvas(added, registerCanvas);
        }
        for (const removed of mutation.removedNodes) {
          forEachCanvas(removed, unregisterCanvas);
        }
      }
    });
    mutationObserver.observe(rootElement || document.body, {
      childList: true,
      subtree: true,
    });
  }
}

export function mountApp(container = document.getElementById('react-mount')) {
  if (!container) return null;
  if (container.style?.visibility === 'hidden') {
    container.style.removeProperty('visibility');
  }
  const app = (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
  const hasPrerenderedDom = container.firstElementChild !== null;
  const root = hasPrerenderedDom
    ? hydrateRoot(container, app)
    : createRoot(container);
  if (!hasPrerenderedDom) {
    root.render(app);
  }
  if (!globalThis.__PRERENDER__) {
    observeFractalCanvases(container);
  }
  return root;
}

const container = typeof document !== 'undefined' ? document.getElementById('react-mount') : null;
if (container) {
  mountApp(container);
}
